import React from 'react';
import { BookOpen, CalendarClock, Flag, ChevronLeft, ChevronRight, MapPin, User, Lock } from 'lucide-react';

export const TYPES = {
  lesson: { label: 'Lesson', color: '#922A8C', soft: '#F6EAF5', icon: BookOpen },
  deadline: { label: 'Course Deadline', color: '#D9480F', soft: '#FFF0E6', icon: Flag },
  appointment: { label: 'Appointment', color: '#0E8C8C', soft: '#E3F5F5', icon: CalendarClock },
};
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const parseDay = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
export const dayKey = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
export const longDate = (iso) => parseDay(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const time12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export const Legend = () => (
  <div className="flex flex-wrap gap-2" data-testid="hc-calendar-legend">
    {Object.entries(TYPES).map(([k, t]) => (
      <span key={k} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: t.soft, color: t.color }}>
        <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />{t.label}
      </span>
    ))}
  </div>
);

export const MonthGrid = ({ month, setMonth, byDay, today, selected, onSelect }) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const cells = Array.from({ length: 42 }, (_, i) => new Date(first.getFullYear(), first.getMonth(), i - first.getDay() + 1));
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2 sm:p-5 min-w-0" data-testid="hc-calendar-month">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base md:text-lg font-bold text-[#363636]" data-testid="hc-calendar-month-label">{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
        <div className="flex gap-2">
          <button onClick={() => shift(-1)} className="p-2 rounded-lg hover:bg-[#F6EAF5]" aria-label="Previous month" data-testid="hc-calendar-prev"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setMonth(new Date(parseDay(today).getFullYear(), parseDay(today).getMonth(), 1))} className="px-3 rounded-lg text-sm font-semibold text-[#922A8C] hover:bg-[#F6EAF5]" data-testid="hc-calendar-today">Today</button>
          <button onClick={() => shift(1)} className="p-2 rounded-lg hover:bg-[#F6EAF5]" aria-label="Next month" data-testid="hc-calendar-next"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-400 mb-1">{WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {cells.map((d) => {
          const k = dayKey(d);
          const list = byDay[k] || [];
          const types = [...new Set(list.map((i) => i.type))];
          const out = d.getMonth() !== month.getMonth();
          return (
            <button key={k} onClick={() => onSelect(k)} data-testid={`hc-calendar-day-${k}`}
              className={`min-h-[56px] sm:min-h-[78px] min-w-0 overflow-hidden rounded-xl p-1 sm:p-1.5 text-left flex flex-col transition-colors ${selected === k ? 'ring-2 ring-[#922A8C] bg-[#F6EAF5]' : 'hover:bg-gray-50'} ${out ? 'opacity-40' : ''}`}>
              <span className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-full ${k === today ? 'bg-[#922A8C] text-white' : 'text-[#363636]'}`}>{d.getDate()}</span>
              <span className="mt-auto flex flex-wrap gap-1">
                {types.map((t) => (
                  <span key={t} className="text-[10px] font-bold rounded px-1 leading-4" style={{ background: TYPES[t].soft, color: TYPES[t].color }}>
                    {list.filter((i) => i.type === t).length}<span className="hidden sm:inline"> {t === 'deadline' ? 'due' : t === 'lesson' ? (list.filter((i) => i.type === t).length === 1 ? 'lesson' : 'lessons') : 'appt'}</span>
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const ItemRow = ({ item, onOpen, showDate }) => {
  const t = TYPES[item.type];
  const Icon = t.icon;
  const clickable = item.type !== 'appointment' && !item.locked;
  return (
    <div className={`flex gap-3 p-3 rounded-xl border-l-4 bg-white ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''}`} style={{ borderColor: t.color }}
      onClick={clickable ? () => onOpen(item) : undefined} data-testid={`hc-calendar-item-${item.id}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.soft, color: t.color }}><Icon className="w-4 h-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide font-bold" style={{ color: t.color }} data-testid={`hc-calendar-item-type-${item.id}`}>{t.label}</span>
          {item.overdue && item.type !== 'appointment' && <span className="text-[10px] uppercase font-bold text-red-600">Past due</span>}
          {item.locked && <span className="text-[10px] uppercase font-bold text-gray-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>}
        </div>
        <div className="font-semibold text-[#363636] truncate">{item.title}</div>
        <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          {showDate && <span>{longDate(item.date)}</span>}
          {item.type === 'appointment' ? (
            <>
              <span>{time12(item.start_time)}{item.end_time ? ` – ${time12(item.end_time)}` : ''}{item.time_zone_abbr ? ` ${item.time_zone_abbr}` : ''}</span>
              {(item.location || item.location_type) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[item.location_type, item.location].filter(Boolean).join(' · ')}</span>}
              {item.staff_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{item.staff_name}</span>}
            </>
          ) : (
            <span>{item.type === 'deadline' ? `${item.remaining_lessons} lesson${item.remaining_lessons === 1 ? '' : 's'} left` : item.course_title}</span>
          )}
        </div>
      </div>
    </div>
  );
};
